import PropTypes from 'prop-types';
import { Dropzone } from '@mantine/dropzone';
import {
  Badge,
  Center,
  Flex,
  Stack,
  Text,
} from '@mantine/core';
import prepareTranslate from '@/helpers/dictionary';
import { notifications } from '@mantine/notifications';
import { CUSTOM_MIME_TYPES } from '@/helpers/enums';
import clsx from 'clsx';
import { IconCloudUpload, IconX } from '@tabler/icons-react';
import { useState } from 'react';
import styles from './style.module.css';
import vars from '../../config/vars';

const CustomDropZone = (props) => {
  const {
    handleAccept = () => {},
    disabled = false,
    outsideAccept = null,
    title = null,
    showFormats = false,
  } = props;

  const [isDragOver, setIsDragOver] = useState(false);
  const [isDropped, setIsDropped] = useState(false);

  const t = prepareTranslate();
  const { maxFileSize } = vars;

  const acceptObjectMimeTypes = {};
  const maxFileSizeInBytes = maxFileSize * 1024 ** 2;

  const dropZoneClass = clsx(
    styles.dropzoneBase,
    disabled && styles.drop_zone,
    isDragOver && styles.drag_over,
    isDropped && !disabled && styles.dropped,
  );
  // eslint-disable-next-line max-len
  const acceptArray = outsideAccept || Object.entries(CUSTOM_MIME_TYPES).map(([, mimeType]) => mimeType);

  // eslint-disable-next-line max-len
  acceptArray.forEach(({ mimeType, extensions }) => { acceptObjectMimeTypes[mimeType] = extensions; });
  const acceptArrayExtensions = acceptArray.flatMap(({ extensions }) => extensions).join(', ');

  const handleOnReject = (files) => {
    setIsDragOver(false);
    setIsDropped(false);

    const overSizeFileNames = files.filter((item) => item.errors.some(({ code: errorCode }) => errorCode.includes('large'))).map(({ file: { name: fileName } }) => fileName).join(', ');
    const invalidFormatFileNames = files.filter((item) => item.errors.some(({ code: errorCode }) => errorCode.includes('type'))).map(({ file: { name: fileName } }) => fileName).join(', ');

    if (overSizeFileNames.length !== 0) {
      notifications.show({
        title: t('components.customDropZone.notification.invalidFileSize.title'),
        message: `${t('components.customDropZone.notification.invalidFileSize.message')} ${overSizeFileNames}`,
        autoClose: false,
      });
    }

    if (invalidFormatFileNames.length !== 0) {
      const InvalidFormatFileMessage = () => (
        <Stack>
          <Text c='black' size='14px'>{t('components.customDropZone.notification.invalidFileFormat.message')} {invalidFormatFileNames}</Text>
          <Text c='black' size='14px'>{t('components.customDropZone.notification.invalidFileFormat.allow')} {acceptArrayExtensions}</Text>
        </Stack>
      );

      notifications.show({
        title: t('components.customDropZone.notification.invalidFileFormat.title'),
        message: <InvalidFormatFileMessage />,
        autoClose: false,
      });
    }
  };

  const handleDragEnter = () => {
    if (!disabled) {
      setIsDragOver(true);
      setIsDropped(false);
    }
  };

  const handleDragLeave = () => {
    if (!disabled) {
      setIsDragOver(false);
    }
  };

  const handleDrop = (files) => {
    setIsDragOver(false);
    setIsDropped(true);
    handleAccept(files);
  };

  return (
          <Dropzone
            disabled={disabled}
            loading={false}
            onDrop={handleDrop}
            onReject={(files) => handleOnReject(files)}
            onDragEnter={handleDragEnter}
            onDragLeave={handleDragLeave}
            maxSize={maxFileSizeInBytes}
            className={dropZoneClass}
            accept={{ ...acceptObjectMimeTypes }}
          >
            <Dropzone.Reject>
              <Center>
                <IconX
                  size={20}
                  stroke={1.5}
                />
                <Text>
                  {t('components.customDropZone.reject.inValidFileFormat')}
                </Text>
              </Center>
            </Dropzone.Reject>
            <Flex
            direction='column'
            align='center'
            gap='sm'
            justify='center'
            h={'100%'}
          >
            {showFormats && <IconCloudUpload size={36} stroke={1.5} />}
            <Text size="lg" inline c={'var(--mantine-color-brand-5)'} fw={'600'}>{title || t('components.files.title')}</Text>
            <Text size='md' c='var(--mantine-color-secondary-0)' inline mt={7}>{`${t('components.files.subtitle')} ${maxFileSize} מגה בייט`}</Text>
            {showFormats && (
              <Flex gap="xs" justify="center">
                {acceptArray.flatMap(({ extensions }) => extensions).map((ext) => (
                  <Badge key={ext} variant="light" color="gray" tt="none">{ext.replace('.', '')}</Badge>
                ))}
              </Flex>
            )}
          </Flex>
          </Dropzone>
  );
};

CustomDropZone.propTypes = {
  handleAccept: PropTypes.func,
  disabled: PropTypes.bool,
  outsideAccept: PropTypes.array,
  title: PropTypes.string,
  showFormats: PropTypes.bool,
};

export default CustomDropZone;
